import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/drodown-menu-custom';
import { FlexCol, FlexRow } from '@/components/ui/flex';
import { Tabs, TabsContent, TabsList } from '@/components/ui/tabs';
import { Text } from '@/components/ui/text';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { literatureReviewPaths } from '@/paths';
import {
  useCreateAbstractScreeningService,
  useDeleteLiteratureSearchService,
  useExportLiteratureSearchesService,
  useGetLiteratureReviewService,
  useLiteratureReviewSearchesService,
  useUpsertAndExecuteLiteratureReviewSearchesService,
} from '@/services/literatureReviews';
import { databases, type Database } from '@ditto/shared';
import {
  Check,
  Download,
  EllipsisVertical,
  Info,
  Plus,
  Trash2,
} from 'lucide-react';
import { useEffect, useMemo, useState, type FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CompleteResultsTab } from './components/CompleteReultsTab';
import { DatabaseResultsTab } from './components/DatabaseResultsTab';
import { CustomTabTrigger } from './components/TabTrigger';
import { WarningModal } from './components/WarningModal';

export const LiteratureReviewSearchStepPage: FC = () => {
  const { literatureReview_id } = useParams();
  const { literatureReviewSearches, stats } =
    useLiteratureReviewSearchesService(literatureReview_id);
  const {
    upsertAndExecuteLiteratureReviewSearches,
    isPending: isSearchPending,
  } = useUpsertAndExecuteLiteratureReviewSearchesService(literatureReview_id);
  const { exportLiteratureSearches, isExportPending } =
    useExportLiteratureSearchesService(literatureReview_id);
  const { createAbstractScreening } = useCreateAbstractScreeningService();
  const { literatureReview } =
    useGetLiteratureReviewService(literatureReview_id);
  const navigate = useNavigate();

  const { deleteLiteratureSearch } =
    useDeleteLiteratureSearchService(literatureReview_id);

  const [queries, setQueries] = useState<Record<Database, string>>(
    databases.reduce(
      (acc, database) => ({ ...acc, [database]: '' }),
      {} as Record<Database, string>
    )
  );

  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);

  const [activeDatabases, setActiveDatabases] = useState<
    (Database | 'unknown')[]
  >(literatureReviewSearches.map(search => search.source) ?? []);

  const availableDatabases = useMemo<Database[]>(
    () => databases.filter(database => !activeDatabases.includes(database)),
    [activeDatabases]
  );

  const [selectedTab, setSelectedTab] = useState<'all' | Database | 'unknown'>(
    'all'
  );

  const handleTabChange = (value: string) => {
    if (value !== 'all' && !activeDatabases.includes(value as Database)) {
      return;
    }
    setSelectedTab(value as 'all' | Database);
  };

  const handleAddDatabaseClick = (database: Database) => {
    setSelectedTab(database);
    setActiveDatabases(prev =>
      prev.includes(database) ? prev : [...prev, database]
    );
  };

  const handleDeleteDatabaseClick = (database: Database | 'unknown') => () => {
    if (database === 'unknown') {
      return;
    }

    if (!literatureReviewSearches?.find(search => search.source === database)) {
      setActiveDatabases(prev => prev.filter(db => db !== database));
      setSelectedTab('all');
      return;
    }

    deleteLiteratureSearch(
      { database },
      {
        onError: () => {
          toast.error('Failed to delete database');
        },
      }
    );
    setActiveDatabases(prev => prev.filter(db => db !== database));
    setSelectedTab('all');
  };

  const handleQueryChange = (database: Database) => (query: string) => {
    setQueries(prev => ({ ...prev, [database]: query }));
  };

  const runSearches = () => {
    if (!literatureReview_id) {
      return;
    }
    upsertAndExecuteLiteratureReviewSearches(
      {
        searches: Object.entries(queries).map(([database, query]) => ({
          database: database as Database,
          query,
        })),
      },
      {
        onError: () => {
          toast.error(
            'Failed to run searches. Make sure your query is in a valid format.'
          );
        },
      }
    );
  };

  const saveSearchAndContinueToScreening = () => {
    if (!literatureReview_id) {
      return;
    }
    const areQueriesUpdated =
      Object.entries(queries).some(
        ([database, query]) =>
          query !==
          literatureReviewSearches.find(search => search.source === database)
            ?.query
      ) ||
      (literatureReview?.abstractScreening &&
        !literatureReview?.abstractScreening?.hasSameWorksAsSearchStep);
    if (literatureReview?.abstractScreening && areQueriesUpdated) {
      setIsWarningModalOpen(true);
      return;
    }
    upsertAndExecuteLiteratureReviewSearches(
      {
        searches: Object.entries(queries).map(([database, query]) => ({
          database: database as Database,
          query,
        })),
      },
      {
        onSuccess: () => {
          if (!areQueriesUpdated && literatureReview?.abstractScreening) {
            navigate(`../${literatureReviewPaths.steps.abstractScreening}`);
            return;
          }
          createAbstractScreening(
            {
              literatureReviewId: literatureReview_id,
              data: {
                criteria: [],
              },
            },
            {
              onSuccess: () => {
                navigate(`../${literatureReviewPaths.steps.abstractScreening}`);
              },
            }
          );
        },
        onError: () => {
          toast.error(
            'Failed to run searches. Make sure your query is in a valid format.'
          );
        },
      }
    );
  };

  const handleWarningModalConfirm = () => {
    if (!literatureReview_id) {
      return;
    }
    upsertAndExecuteLiteratureReviewSearches(
      {
        searches: Object.entries(queries).map(([database, query]) => ({
          database: database as Database,
          query,
        })),
      },
      {
        onSuccess: () => {
          createAbstractScreening(
            {
              literatureReviewId: literatureReview_id,
              data: {
                criteria: [],
              },
            },
            {
              onSuccess: () => {
                navigate(`../${literatureReviewPaths.steps.abstractScreening}`);
              },
            }
          );
        },
        onError: () => {
          toast.error(
            'Failed to run searches. Make sure your query is in a valid format.'
          );
        },
      }
    );
  };

  const handleExportClick = () => {
    if (!literatureReview_id) {
      return;
    }
    exportLiteratureSearches({
      onError: () => {
        toast.error('Failed to export literature review searches');
      },
    });
  };

  useEffect(() => {
    setActiveDatabases(literatureReviewSearches.map(search => search.source));
    setQueries(
      literatureReviewSearches.reduce(
        (acc, search) => ({ ...acc, [search.source]: search.query }),
        {} as Record<Database, string>
      )
    );
  }, [literatureReviewSearches]);

  return (
    <FlexCol className="flex flex-col">
      {/* Main content area */}
      <FlexCol>
        <FlexCol p="6" gap="6">
          <FlexRow alignItems="center" justifyContent="between">
            <Text variant="header1" color="primary">
              Search Papers
            </Text>
            <Button
              variant="secondary"
              loading={isExportPending}
              onClick={handleExportClick}
            >
              <Download />
              Export list of papers
            </Button>
          </FlexRow>
          <Tabs value={selectedTab} onValueChange={handleTabChange}>
            <FlexRow className="items-center justify-start w-full gap-4 mb-2 border-b">
              <TabsList variant="underline">
                <CustomTabTrigger
                  value="all"
                  label="All"
                  count={stats?.total - stats?.invalid - stats?.duplicates || 0}
                  isSelected={selectedTab === 'all'}
                />
                {activeDatabases.map(database => {
                  const databaseData = literatureReviewSearches.find(
                    s => s.source === database
                  );
                  const count = databaseData?.workCount || 0;
                  return (
                    <FlexRow key={database} alignItems="center">
                      <CustomTabTrigger
                        key={database}
                        value={database}
                        label={database}
                        count={count}
                        isSelected={selectedTab === database}
                      />
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild className="mb-4">
                          <Button variant="ghost" size="icon">
                            <EllipsisVertical className="w-3 h-3 fill-neutral-400" />
                          </Button>
                        </DropdownMenuTrigger>

                        <DropdownMenuContent align="start" className="w-48">
                          <DropdownMenuItem
                            variant="destructive"
                            onClick={handleDeleteDatabaseClick(database)}
                          >
                            <Trash2 className="w-3 h-3 text-red-500" />
                            Delete this database
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </FlexRow>
                  );
                })}
              </TabsList>
              {!!availableDatabases?.length && (
                <FlexCol className="items-start justify-start h-full pb-4">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="secondary"
                        size="sm"
                        className="rounded-xl"
                      >
                        <Plus className="w-3 h-3" />
                        <Text className="text-sm font-medium">
                          Add database
                        </Text>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-48">
                      {availableDatabases.map(database => (
                        <DropdownMenuItem
                          key={database}
                          onClick={() => handleAddDatabaseClick(database)}
                          className="cursor-pointer"
                        >
                          <Text className="text-sm capitalize">{database}</Text>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </FlexCol>
              )}
            </FlexRow>

            <TabsContent value="all">
              <CompleteResultsTab onDatabaseClick={handleAddDatabaseClick} />
            </TabsContent>
            {activeDatabases.map(database =>
              database !== 'unknown' ? (
                <TabsContent key={database} value={database}>
                  <DatabaseResultsTab
                    database={database}
                    query={queries[database]}
                    onQueryChange={handleQueryChange(database)}
                    onRunSearchesClick={runSearches}
                    isSearchPending={isSearchPending}
                  />
                </TabsContent>
              ) : null
            )}
          </Tabs>
        </FlexCol>
      </FlexCol>

      {/* Sticky footer */}
      {stats && literatureReviewSearches.length > 0 && (
        <div className="sticky bottom-0 bg-white border-t border-neutral-200 z-10">
          <FlexCol className="w-full h-fit align-middle justify-center">
            <FlexCol className="gap-3 align-middle justify-center w-full items-center mt-3">
              {stats.total > 0 && (
                <FlexRow className="items-center gap-[8px] justify-center w-full">
                  <Check className="w-4 h-4 text-green-500" />
                  <Text className="text-sm font-semibold">
                    {stats.total - stats.duplicates - stats.invalid} articles
                    found
                  </Text>
                  <Text className="text-sm text-neutral-500">{`(${stats.total} retrieved > Removed ${stats.invalid} invalid > Removed ${stats.duplicates} duplicates)`}</Text>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="w-4 h-4 text-neutral-500" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <Text className="text-sm text-white">
                        Articles may be considered invalid if key information,
                        such as DOI, is missing.
                      </Text>
                    </TooltipContent>
                  </Tooltip>
                </FlexRow>
              )}
              {stats.total === 0 && (
                <FlexRow className="items-center gap-[8px] justify-center w-full">
                  <Text className="text-sm text-neutral-500">
                    No articles found
                  </Text>
                </FlexRow>
              )}
              <Button
                onClick={saveSearchAndContinueToScreening}
                loading={isSearchPending}
              >
                Save search and continue to screening
              </Button>
            </FlexCol>
          </FlexCol>
        </div>
      )}
      {literatureReview?.abstractScreening && (
        <WarningModal
          isOpen={isWarningModalOpen}
          onOpenChange={setIsWarningModalOpen}
          isLoading={isSearchPending}
          onConfirm={handleWarningModalConfirm}
          criteriaCount={literatureReview.abstractScreening.criteriaCount}
          screeningDecisionsCount={
            literatureReview.abstractScreening.screeningDecisionsCount
          }
          validatedCount={literatureReview.abstractScreening.validatedCount}
        />
      )}
    </FlexCol>
  );
};
